import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Table, Form } from 'react-bootstrap';
import Select from 'react-select';
import { Card } from 'react-bootstrap-v5';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { numberFormat, getThisFiscalYearRange } from '../helpers/GlobalHelper';
import { postRequestOptions, getRequestOptions } from '../helpers/Fetchwrapper';
import { Link, useLocation } from 'react-router-dom';
import { ReportSelectOption } from './LedgerStatementReport';

type ProjectOption = ReportSelectOption & { accounting_startup_date?: string | null };

const defaultProjectOption: ProjectOption = { label: 'Select Project', value: '' };
const fiscalYearRange = getThisFiscalYearRange();

type ViewMode = 'full' | 'immediate';

interface GroupWiseRow {
    idAccLedgers: number;
    parentId: number;
    ledgerCode: string;
    ledgerName: string;
    groupPath: string;
    ledgerType: 'group' | 'gl' | string;
    category: string;
    level: number;
    isImmediateParent: boolean;
    openingDr: number;
    openingCr: number;
    transactionDr: number;
    transactionCr: number;
    closingDr: number;
    closingCr: number;
}

interface GroupWiseTotals {
    openingDr: number;
    openingCr: number;
    transactionDr: number;
    transactionCr: number;
    closingDr: number;
    closingCr: number;
}

interface GroupWiseHeader {
    projectName?: string | null;
    fromDate: string;
    toDate: string;
}

interface GroupWisePayload {
    header: GroupWiseHeader;
    rows: GroupWiseRow[];
    totals: GroupWiseTotals;
}

const CATEGORY_ORDER = ['asset', 'expense', 'liability', 'revenue'];
const CATEGORY_LABELS: Record<string, string> = {
    asset: 'Asset',
    expense: 'Expense',
    liability: 'Liability',
    revenue: 'Revenue',
};

const emptyTotals: GroupWiseTotals = {
    openingDr: 0,
    openingCr: 0,
    transactionDr: 0,
    transactionCr: 0,
    closingDr: 0,
    closingCr: 0,
};

const formatAmount = (value: number | undefined) => numberFormat(Number(value || 0));

const normalizeSearch = (value: unknown) => String(value || '').toLowerCase().trim();

const rowMatchesKeyword = (row: GroupWiseRow, keyword: string) => {
    if (!keyword) {
        return true;
    }

    const name = normalizeSearch(row.ledgerName);
    const code = normalizeSearch(row.ledgerCode);
    const path = normalizeSearch(row.groupPath);
    const category = normalizeSearch(row.category);

    return (
        name.includes(keyword) ||
        code.includes(keyword) ||
        path.includes(keyword) ||
        category.includes(keyword)
    );
};

const isCodeAncestorOf = (maybeAncestor: string, maybeDescendant: string) => {
    if (!maybeAncestor || !maybeDescendant) {
        return false;
    }
    return maybeDescendant === maybeAncestor || maybeDescendant.startsWith(`${maybeAncestor}.`);
};

const GroupWiseBalanceReport: React.FC = () => {
    const location = useLocation();
    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const [loading, setLoading] = useState(false);
    const [projects, setProjects] = useState<ProjectOption[]>([]);
    const [project, setProject] = useState<ProjectOption>(defaultProjectOption);
    const [reportData, setReportData] = useState<GroupWisePayload | null>(null);
    const [viewMode, setViewMode] = useState<ViewMode>('immediate');
    const [searchText, setSearchText] = useState('');
    const [collapsedCodes, setCollapsedCodes] = useState<string[]>([]);

    const [formData, setFormData] = useState({
        idProjects: query.get('idProjects') ?? '',
        fromDate: query.get('fromDate') ?? fiscalYearRange.fromDate,
        toDate: query.get('toDate') ?? fiscalYearRange.toDate,
    });

    const hasInitialQuery = useMemo(
        () => Boolean(query.get('idProjects') && query.get('fromDate') && query.get('toDate')),
        [query]
    );

    const autoLoadTriggeredRef = useRef(false);

    const fetchProjects = () => {
        fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                const projectOptions: ProjectOption[] = (resp?.data || []).map((item: any) => ({
                    label: item.name,
                    value: item.id,
                    accounting_startup_date: item.accounting_startup_date ?? '',
                }));

                setProjects(projectOptions);

                const queryProjectId = query.get('idProjects');
                let nextProject = defaultProjectOption;

                if (queryProjectId) {
                    nextProject =
                        projectOptions.find((opt) => String(opt.value) === String(queryProjectId)) ?? defaultProjectOption;
                } else if (projectOptions.length > 0) {
                    nextProject = projectOptions[0];
                }

                setProject(nextProject);
                setFormData((prev) => ({
                    ...prev,
                    idProjects: nextProject.value !== '' ? String(nextProject.value) : '',
                    fromDate: query.get('fromDate') || nextProject.accounting_startup_date || prev.fromDate,
                }));
            })
            .catch((error) => {
                console.log(error, 'catch the hoop');
            });
    };

    useEffect(() => {
        fetchProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleProjectChange = (selectedProject: ProjectOption | null) => {
        const nextProject = selectedProject ?? defaultProjectOption;
        setProject(nextProject);
        setFormData((prev) => ({
            ...prev,
            idProjects: nextProject.value !== '' ? String(nextProject.value) : '',
            fromDate: nextProject.accounting_startup_date || prev.fromDate,
        }));
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleViewReport = useCallback(async () => {
        if (!formData.idProjects) {
            toast.error('Project is required.', { position: 'bottom-right', autoClose: 4000 });
            return;
        }
        if (!formData.fromDate || !formData.toDate) {
            toast.error('From Date and To Date are required.', { position: 'bottom-right', autoClose: 4000 });
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL + 'reports/group-wise-balance',
                postRequestOptions(formData)
            );
            const data = await response.json();

            if (data.success) {
                setReportData(data.data || null);
                setCollapsedCodes([]);
                setSearchText('');
            } else {
                setReportData(null);
                const errors = data?.errors;
                const messages = Array.isArray(errors)
                    ? errors
                    : errors
                        ? Object.values(errors).flat()
                        : [data?.errorMessage || data?.message || 'Failed to load report.'];
                messages.forEach((error: string) =>
                    toast.error(error, { position: 'bottom-right', autoClose: 4000 })
                );
            }
        } catch (error) {
            console.error('Error fetching report:', error);
            toast.error('Failed to load report. Please try again later.', {
                position: 'bottom-right',
                autoClose: 4000,
            });
        } finally {
            setLoading(false);
        }
    }, [formData]);

    useEffect(() => {
        if (
            hasInitialQuery &&
            !autoLoadTriggeredRef.current &&
            formData.idProjects &&
            formData.fromDate &&
            formData.toDate
        ) {
            handleViewReport();
            autoLoadTriggeredRef.current = true;
        }
    }, [formData.idProjects, formData.fromDate, formData.toDate, hasInitialQuery, handleViewReport]);

    const visibleRows = useMemo(() => {
        const rows = reportData?.rows || [];
        const keyword = normalizeSearch(searchText);

        const scopedRows = viewMode === 'immediate'
            ? rows.filter((row) => row.isImmediateParent)
            : rows;

        if (!keyword) {
            if (viewMode === 'immediate' || collapsedCodes.length === 0) {
                return scopedRows;
            }

            return scopedRows.filter((row) =>
                !collapsedCodes.some((code) => (row.ledgerCode || '').startsWith(`${code}.`))
            );
        }

        const directMatches = scopedRows.filter((row) => rowMatchesKeyword(row, keyword));

        if (viewMode === 'immediate') {
            return directMatches;
        }

        const matchingCodes = directMatches.map((row) => row.ledgerCode).filter(Boolean);
        const matchingGroupCodes = directMatches
            .filter((row) => row.ledgerType === 'group')
            .map((row) => row.ledgerCode)
            .filter(Boolean);

        return scopedRows.filter((row) => {
            const code = row.ledgerCode || '';

            const isDirectMatch = matchingCodes.includes(code);
            const isAncestorOfMatch = matchingCodes.some((matchCode) => isCodeAncestorOf(code, matchCode));
            const isDescendantOfMatchedGroup = matchingGroupCodes.some((groupCode) => isCodeAncestorOf(groupCode, code));

            return isDirectMatch || isAncestorOfMatch || isDescendantOfMatchedGroup;
        });
    }, [reportData, viewMode, searchText, collapsedCodes]);

    const rowsByCategory = useMemo(() => {
        const grouped: Record<string, GroupWiseRow[]> = {};
        visibleRows.forEach((row) => {
            const category = row.category || 'other';
            if (!grouped[category]) {
                grouped[category] = [];
            }
            grouped[category].push(row);
        });
        return grouped;
    }, [visibleRows]);

    const categoryKeys = useMemo(() => {
        const extra = Object.keys(rowsByCategory).filter((key) => !CATEGORY_ORDER.includes(key));
        return [...CATEGORY_ORDER.filter((key) => rowsByCategory[key]?.length), ...extra];
    }, [rowsByCategory]);

    const totals = useMemo(() => {
        const source = viewMode === 'immediate'
            ? visibleRows
            : visibleRows.filter((row) => row.ledgerType === 'gl');

        return source.reduce(
            (acc, row) => ({
                openingDr: acc.openingDr + Number(row.openingDr || 0),
                openingCr: acc.openingCr + Number(row.openingCr || 0),
                transactionDr: acc.transactionDr + Number(row.transactionDr || 0),
                transactionCr: acc.transactionCr + Number(row.transactionCr || 0),
                closingDr: acc.closingDr + Number(row.closingDr || 0),
                closingCr: acc.closingCr + Number(row.closingCr || 0),
            }),
            { ...emptyTotals }
        );
    }, [visibleRows, viewMode]);

    const queryParams = useMemo(() => {
        const params = new URLSearchParams();
        Object.entries(formData).forEach(([key, value]) => {
            if (value) {
                params.append(key, String(value));
            }
        });
        params.append('viewMode', viewMode);
        if (searchText.trim()) {
            params.append('search', searchText.trim());
        }
        return params;
    }, [formData, viewMode, searchText]);

    const toggleCollapse = (row: GroupWiseRow) => {
        if (row.ledgerType !== 'group') {
            return;
        }
        setCollapsedCodes((prev) =>
            prev.includes(row.ledgerCode)
                ? prev.filter((code) => code !== row.ledgerCode)
                : [...prev, row.ledgerCode]
        );
    };

    const handlePrint = () => {
        if (!formData.idProjects || !formData.fromDate || !formData.toDate) {
            toast.error('Project, From Date, and To Date are required.', { position: 'bottom-right', autoClose: 4000 });
            return;
        }
        if (!reportData) {
            toast.error('Please load the report before printing.', { position: 'bottom-right', autoClose: 4000 });
            return;
        }
        window.location.href = `${process.env.REACT_APP_BACKEND}group_wise_balance_report_print?` + queryParams.toString();
    };

    const renderAmountCells = (row: GroupWiseRow, isGroup: boolean) => {
        const className = `text-end ${isGroup ? 'fw-bold' : ''}`;
        return (
            <>
                <td className={className}>{formatAmount(row.openingDr)}</td>
                <td className={className}>{formatAmount(row.openingCr)}</td>
                <td className={className}>{formatAmount(row.transactionDr)}</td>
                <td className={className}>{formatAmount(row.transactionCr)}</td>
                <td className={className}>{formatAmount(row.closingDr)}</td>
                <td className={className}>{formatAmount(row.closingCr)}</td>
            </>
        );
    };

    return (
        <>
            <Card className='mb-4 group-wise-balance-filters'>
                <Card.Body>
                    <div className='row'>
                        <div className='col-md-3'>
                            <Form.Group className="mb-3" controlId="project">
                                <Form.Label>Project<span className='required'></span></Form.Label>
                                <Select
                                    value={project}
                                    onChange={handleProjectChange}
                                    options={projects}
                                    menuPosition="fixed"
                                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                    isClearable={true}
                                />
                            </Form.Group>
                        </div>
                        <div className='col-md-2'>
                            <Form.Group className="mb-3" controlId="fromDate">
                                <Form.Label>From Date<span className='required'></span></Form.Label>
                                <Form.Control
                                    type="date"
                                    name='fromDate'
                                    value={formData.fromDate}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </div>
                        <div className='col-md-2'>
                            <Form.Group className="mb-3" controlId="toDate">
                                <Form.Label>To Date<span className='required'></span></Form.Label>
                                <Form.Control
                                    type="date"
                                    name='toDate'
                                    value={formData.toDate}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </div>
                        <div className='col-md-5'>
                            <div className="d-flex flex-wrap gap-2 mt-7">
                                <button
                                    type='button'
                                    className='btn btn-sm btn-primary p-3 me-2'
                                    onClick={handleViewReport}
                                    disabled={loading}
                                >
                                    {loading && (
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                    )}
                                    <span>View Report</span>
                                </button>
                                <button
                                    type='button'
                                    className='btn btn-sm btn-light-primary p-3 me-2'
                                    onClick={handlePrint}
                                    disabled={loading || !reportData}
                                >
                                    <i className='fas fa-print me-1'></i>
                                    Print PDF
                                </button>
                                <a
                                    className={`btn btn-sm btn-light-primary p-3 ${!formData.idProjects || !formData.fromDate || !formData.toDate ? 'disabled' : ''}`}
                                    href={`${process.env.REACT_APP_BACKEND}group_wise_balance_report_download?` + queryParams.toString()}
                                >
                                    <i className='fas fa-download me-1'></i>
                                    Excel
                                </a>
                            </div>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            {reportData?.header && (
                <div className='group-wise-balance-header text-center mb-4'>
                    <h4 className='fw-bolder mb-1'>Group-wise Balance Report</h4>
                    <div className='text-gray-700'>{reportData.header.projectName}</div>
                    <div className='fs-7 text-muted'>
                        Period: {reportData.header.fromDate} to {reportData.header.toDate}
                    </div>
                </div>
            )}

            {reportData && (
                <div className='d-flex flex-wrap align-items-center justify-content-between mb-3 group-wise-balance-toolbar'>
                    <div className='d-flex flex-wrap align-items-center'>
                        <div className='btn-group me-4 mb-2' role='group'>
                            <button
                                type='button'
                                className={`btn btn-sm ${viewMode === 'immediate' ? 'btn-primary' : 'btn-light-primary'}`}
                                onClick={() => setViewMode('immediate')}
                            >
                                Immediate Parent Groups
                            </button>
                            <button
                                type='button'
                                className={`btn btn-sm ${viewMode === 'full' ? 'btn-primary' : 'btn-light-primary'}`}
                                onClick={() => setViewMode('full')}
                            >
                                Full Hierarchy
                            </button>
                        </div>
                        <div className='d-flex align-items-center fs-8 text-muted mb-2'>
                            {viewMode === 'immediate' ? (
                                <span>Only immediate parent groups are shown. Ledgers are hidden.</span>
                            ) : (
                                <>
                                    <span className='me-3'><strong>Bold</strong> = Group</span>
                                    <span className='me-3' style={{ color: '#c2185b' }}>Pink</span> = Ledger
                                    <span className='ms-3'>Highlighted = Immediate parent</span>
                                </>
                            )}
                        </div>
                    </div>
                    <div className='mb-2' style={{ minWidth: '240px' }}>
                        <Form.Control
                            type='text'
                            placeholder={viewMode === 'immediate' ? 'Search group or path' : 'Search group or ledger'}
                            value={searchText}
                            onChange={(event) => setSearchText(event.target.value)}
                        />
                    </div>
                </div>
            )}

            <div className='row'>
                <div className='col-12 trial-balance-container table-responsive group-wise-balance-table'>
                    <Table bordered hover size='sm' className='mb-0'>
                        <thead className="sticky-top bg-white">
                            <tr>
                                <th rowSpan={2} className='text-end pe-3' style={{ width: '8%' }}>Ledger Code</th>
                                <th rowSpan={2} style={{ width: viewMode === 'immediate' ? '18%' : '37%' }}>
                                    {viewMode === 'immediate' ? 'Group' : 'Group / Ledger'}
                                </th>
                                {viewMode === 'immediate' && (
                                    <th rowSpan={2} style={{ width: '22%' }}>Group Path</th>
                                )}
                                <th className='text-center' colSpan={2}>Opening Balance</th>
                                <th className='text-center' colSpan={2}>Transaction Balance</th>
                                <th className='text-center' colSpan={2}>Closing Balance</th>
                            </tr>
                            <tr>
                                <th className='text-end'>Debit</th>
                                <th className='text-end'>Credit</th>
                                <th className='text-end'>Debit</th>
                                <th className='text-end'>Credit</th>
                                <th className='text-end'>Debit</th>
                                <th className='text-end'>Credit</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!reportData && (
                                <tr>
                                    <td colSpan={viewMode === 'immediate' ? 9 : 8} className="text-center py-8 text-muted">
                                        Select Project, From Date and To Date, then click View Report.
                                    </td>
                                </tr>
                            )}
                            {reportData && visibleRows.length === 0 && (
                                <tr>
                                    <td colSpan={viewMode === 'immediate' ? 9 : 8} className="text-center py-8 text-muted">No Data Found</td>
                                </tr>
                            )}
                            {categoryKeys.map((category) => (
                                <React.Fragment key={category}>
                                    <tr className='table-secondary'>
                                        <td></td>
                                        <td colSpan={viewMode === 'immediate' ? 8 : 7} className='fw-bolder text-uppercase'>
                                            {CATEGORY_LABELS[category] || category}
                                        </td>
                                    </tr>
                                    {rowsByCategory[category].map((row) => {
                                        const isGroup = row.ledgerType === 'group';
                                        const isCollapsed = collapsedCodes.includes(row.ledgerCode);
                                        const displayLevel = viewMode === 'immediate' ? 0 : row.level;
                                        const paddingLeft = 12 + displayLevel * 18;

                                        return (
                                            <tr
                                                key={`${row.ledgerCode}-${row.idAccLedgers}`}
                                                className={row.isImmediateParent ? 'group-wise-immediate' : ''}
                                            >
                                                <td className='text-end pe-3 text-muted'>{row.ledgerCode}</td>
                                                <td style={{ paddingLeft: `${paddingLeft}px` }}>
                                                    {isGroup ? (
                                                        viewMode === 'immediate' ? (
                                                            <span className='fw-bold text-dark'>{row.ledgerName}</span>
                                                        ) : (
                                                            <button
                                                                type='button'
                                                                className='btn btn-link p-0 text-start text-dark fw-bold text-decoration-none'
                                                                onClick={() => toggleCollapse(row)}
                                                            >
                                                                <i className={`fas fa-caret-${isCollapsed ? 'right' : 'down'} me-2 text-muted`}></i>
                                                                {row.ledgerName}
                                                                {row.isImmediateParent && (
                                                                    <span className='badge badge-light-primary ms-2'>Immediate Group</span>
                                                                )}
                                                            </button>
                                                        )
                                                    ) : (
                                                        <Link
                                                            className='group-wise-ledger'
                                                            to={`reports?type=ledgerStatement&idProjects=${formData.idProjects}&idAccLedgers=${row.idAccLedgers}&fromDate=${formData.fromDate}&toDate=${formData.toDate}`}
                                                        >
                                                            {row.ledgerName}
                                                        </Link>
                                                    )}
                                                </td>
                                                {viewMode === 'immediate' && (
                                                    <td className='text-muted'>
                                                        {row.groupPath || '-'}
                                                    </td>
                                                )}
                                                {renderAmountCells(row, isGroup)}
                                            </tr>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </tbody>
                        {reportData && (
                            <tfoot className='bg-white sticky-bottom'>
                                <tr className='fw-bolder'>
                                    <td></td>
                                    <td>Total</td>
                                    {viewMode === 'immediate' && <td></td>}
                                    <td className='text-end'>{formatAmount(totals.openingDr)}</td>
                                    <td className='text-end'>{formatAmount(totals.openingCr)}</td>
                                    <td className='text-end'>{formatAmount(totals.transactionDr)}</td>
                                    <td className='text-end'>{formatAmount(totals.transactionCr)}</td>
                                    <td className='text-end'>{formatAmount(totals.closingDr)}</td>
                                    <td className='text-end'>{formatAmount(totals.closingCr)}</td>
                                </tr>
                            </tfoot>
                        )}
                    </Table>
                </div>
            </div>
        </>
    );
};

export default GroupWiseBalanceReport;
